import { Users, Trophy, Mountain, MapPin } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { DashboardTile } from '../DashboardTile';

// TODO: Connect to Supabase teams and challenges tables
interface Team {
  id: number;
  name: string;
  members: number;
  active: boolean;
}

interface Challenge {
  id: number;
  name: string;
  participants: number;
  endsIn: string;
}

interface ClubhouseActivityTileProps {
  teams?: Team[];
  challenges?: Challenge[];
  onTeamClick?: (team: Team) => void;
  onChallengeClick?: (challenge: Challenge) => void;
}

/**
 * Clubhouse Activity Tile - Teams and challenges preview
 * From FIGMA Clubhouse 2.0 design
 * 
 * @TODO Wire to useQuery for teams and challenges tables
 */
export function ClubhouseActivityTile({
  teams,
  challenges,
  onTeamClick,
  onChallengeClick,
}: ClubhouseActivityTileProps) {
  // Mock data - replace with real data when connected to Supabase
  const defaultTeams: Team[] = [
    { id: 1, name: 'Weekend Warriors', members: 12, active: true },
    { id: 2, name: 'Par Seekers', members: 8, active: true },
  ];

  const defaultChallenges: Challenge[] = [
    { id: 1, name: '100 Swing Challenge', participants: 234, endsIn: '3 days' },
    { id: 2, name: 'Perfect Practice Week', participants: 189, endsIn: '5 days' },
  ];

  const clubhouseTeams = teams || defaultTeams;
  const clubhouseChallenges = challenges || defaultChallenges;

  return (
    <DashboardTile title="Clubhouse Activity" delay={0.2} data-figma-id="clubhouse-activity-tile">
      <Tabs defaultValue="teams" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="teams" className="text-xs">
            <Users className="w-3 h-3 mr-1" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="challenges" className="text-xs">
            <Trophy className="w-3 h-3 mr-1" />
            Challenges
          </TabsTrigger>
          <TabsTrigger value="everests" className="text-xs">
            <Mountain className="w-3 h-3 mr-1" />
            Everests
          </TabsTrigger>
          <TabsTrigger value="courses" className="text-xs">
            <MapPin className="w-3 h-3 mr-1" />
            Courses
          </TabsTrigger>
        </TabsList>

        <TabsContent value="teams" className="mt-4 space-y-3">
          {clubhouseTeams.map((team) => (
            <div
              key={team.id}
              onClick={() => onTeamClick?.(team)}
              className="p-3 bg-gradient-to-r from-[hsl(142,45%,96%)] to-white rounded-lg border border-border hover:border-primary transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <Avatar className="w-6 h-6 border-2 border-white">
                      <AvatarFallback>A</AvatarFallback>
                    </Avatar>
                    <Avatar className="w-6 h-6 border-2 border-white">
                      <AvatarFallback>B</AvatarFallback>
                    </Avatar>
                    <Avatar className="w-6 h-6 border-2 border-white">
                      <AvatarFallback>C</AvatarFallback>
                    </Avatar>
                  </div>
                  <div>
                    <p className="text-sm font-medium">{team.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {team.members} members
                    </p>
                  </div>
                </div>
                {team.active && (
                  <Badge variant="default" className="bg-success">
                    Active
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="challenges" className="mt-4 space-y-3">
          {clubhouseChallenges.map((challenge) => (
            <div
              key={challenge.id}
              onClick={() => onChallengeClick?.(challenge)}
              className="p-3 bg-gradient-to-r from-[hsl(43,80%,96%)] to-white rounded-lg border border-border hover:border-warning transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{challenge.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {challenge.participants} participants
                  </p>
                </div>
                <Badge variant="outline">{challenge.endsIn}</Badge>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="everests" className="mt-4">
          <div className="text-center py-8 text-muted-foreground text-sm">
            <Mountain className="w-12 h-12 mx-auto mb-2 opacity-20" />
            No active Everest challenges
          </div>
        </TabsContent>

        <TabsContent value="courses" className="mt-4">
          <div className="text-center py-8 text-muted-foreground text-sm">
            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-20" />
            Add your home course to get started
          </div>
        </TabsContent>
      </Tabs>
    </DashboardTile>
  );
}

